import { useEffect } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useIsDesktop } from "@/lib/hooks";

const EASE = [0.2, 0.8, 0.2, 1];

/**
 * Page with an optional right panel. On desktop the panel is inline and non-modal, so search,
 * filters, sorting and scroll position in the page survive opening/closing it. On smaller screens
 * the same content opens as a sheet. Escape closes the panel in both cases.
 */
export const SplitPage = ({ children, panel, onClose, title = "Detaljer" }) => {
  const desktop = useIsDesktop();
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!panel || !desktop) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel, desktop, onClose]);

  return (
    <div className="split">
      {children}
      {desktop && (
        <AnimatePresence initial={false}>
          {panel && (
            <motion.aside
              key="panel"
              className="side-panel"
              data-testid="side-panel"
              aria-label={title}
              initial={reduce ? false : { x: 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={reduce ? { opacity: 0 } : { x: 24, opacity: 0 }}
              transition={{ duration: 0.22, ease: EASE }}
            >
              {panel}
            </motion.aside>
          )}
        </AnimatePresence>
      )}
      {!desktop && (
        <Sheet open={!!panel} onOpenChange={(o) => !o && onClose?.()}>
          <SheetContent side="right" className="w-full p-0 sm:max-w-md [&>button]:hidden" data-testid="side-panel-sheet">
            <SheetTitle className="sr-only">{title}</SheetTitle>
            {panel}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

/** Head / scrollable body / footer for a panel. `chip` sits next to the eyebrow (e.g. phase). */
export const PanelFrame = ({ eyebrow, chip, title, meta, onClose, children, footer, testId }) => (
  <div className="panel" data-testid={testId}>
    <div className="sp-head">
      <div className="min-w-0">
        {(eyebrow || chip) && (
          <div className="flex flex-wrap items-center gap-2">
            {eyebrow && <p className="t-label" data-testid="panel-eyebrow">{eyebrow}</p>}
            {chip}
          </div>
        )}
        {title && <h2 className="mt-0.5 text-[18px] font-semibold leading-snug tracking-tight" data-testid="panel-title">{title}</h2>}
        {meta && <p className="t-meta mt-1">{meta}</p>}
      </div>
      <Button variant="ghost" size="icon" className="-mr-2 -mt-1 h-9 w-9 shrink-0" onClick={onClose} aria-label="Lukk panel" data-testid="panel-close"><X /></Button>
    </div>
    <div className="sp-body">{children}</div>
    {footer && <div className="sp-foot">{footer}</div>}
  </div>
);

/** Small labelled block inside a panel. */
export const PanelSection = ({ label, children, className = "", testId }) => (
  <section className={`mt-6 first:mt-0 ${className}`} data-testid={testId}>
    {label && <h3 className="panel-h mb-2">{label}</h3>}
    {children}
  </section>
);

/** Staggered list enter: wrap each row; keeps DOM identical otherwise. */
export const Enter = ({ index = 0, children, className = "", as = "div", max = 8, ...rest }) => {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  const i = Math.min(index, max);
  return (
    <Tag
      className={className}
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: EASE, delay: i * 0.02 }}
      {...rest}
    >
      {children}
    </Tag>
  );
};
